import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import styled, { css, keyframes } from 'styled-components';
import { colors } from '@/constants/colors';
import useAuthStore from '@/store/authStore';
import { useCommunityDrawer } from './drawerStore';
import { useCommunityFeatures } from './useCommunityFeatures';

// The light half of Community: always in the main bundle (header button,
// dock, deep link). The drawer itself — socket.io, the screens, the styles —
// loads on demand.
const CommunityDrawer = lazy(() => import('./CommunityDrawer'));
const CommunityPhonePage = lazy(() => import('./CommunityPhonePage'));
/** Phones get the full page instead of the drawer — the same width where the
 *  bottom menu shows (CommunityDrawer's PHONE_MAX). */
const PHONE_MAX = 768;

/** Mounted once at the app root (tests hand in a fake bridge through BridgeContext). Renders the drawer after a dashboard page has shown, while signed in and Community is ON. */
export function CommunityDock() {
   useUnreadInTabTitle();
   const shell = useCommunityDrawer((s) => s.shell);
   const page = useCommunityDrawer((s) => s.page);
   const signedIn = useAuthStore((s) => s.isAuthenticated && !!s.token && s.user?.verified !== false);
   const on = useCommunityFeatures().on('community');
   // The phone page holds Community while it shows: never two connections.
   if (!shell || !signedIn || !on || page) return null;
   return (
      <Suspense fallback={null}>
         <CommunityDrawer />
      </Suspense>
   );
}

/** "(3) SmartFi" on the browser tab while chat messages are unread, like Discord (Ray 2026-10-27). */
const COUNT_PREFIX = /^\(\d+\+?\) /;
export function useUnreadInTabTitle() {
   const unread = useCommunityDrawer((s) => s.unread);
   useEffect(() => {
      const label = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ` : '';
      const apply = () => {
         const want = label + document.title.replace(COUNT_PREFIX, '');
         if (document.title !== want) document.title = want;
      };
      apply();
      // A page that sets its own title gets the count put back in front.
      const el = document.querySelector('title');
      const watch = el ? new MutationObserver(apply) : null;
      if (el) watch!.observe(el, { childList: true, characterData: true, subtree: true });
      return () => {
         watch?.disconnect();
         document.title = document.title.replace(COUNT_PREFIX, '');
      };
   }, [unread]);
}

/** Dashboard pages call this: from now on Community may connect. */
export function useMarkDashboardShell() {
   useEffect(() => {
      useCommunityDrawer.getState().setShell(true);
   }, []);
}

/** Header chat button: toggles the drawer, shows the unread count while closed (Desktop ChatButton). Not on phones — see useCommunityTab. */
export function CommunityButton() {
   const on = useCommunityFeatures().on('community');
   const phone = useIsMobile(PHONE_MAX);
   const drawerOpen = useCommunityDrawer((s) => s.open);
   const unread = useCommunityDrawer((s) => s.unread);
   const toggle = useCommunityDrawer((s) => s.toggle);
   // Phones reach Community from the bottom menu's Chat tab instead (Ray 2026-10-25).
   if (!on || phone) return null;
   const open = drawerOpen;
   return (
      <ChatBtn type="button" onClick={toggle} aria-label="Community chat" title="Community chat" aria-pressed={open} $alert={!open && unread > 0} data-alert={!open && unread > 0 ? 'unread' : undefined}>
         <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
         </svg>
         {!open && unread > 0 && <Badge data-testid="community-unread">{unread > 9 ? '9+' : unread}</Badge>}
      </ChatBtn>
   );
}

/** The bottom menu's Chat tab on phones (Ray 2026-10-25: 3rd item): opens the
 *  Community page, shows the unread count while you are elsewhere. */
export function useCommunityTab() {
   const on = useCommunityFeatures().on('community');
   const navigate = useNavigate();
   const active = useLocation().pathname === '/wifi-community';
   const unread = useCommunityDrawer((s) => s.unread);
   return { on, active, unread: active ? 0 : unread, open: () => navigate('/wifi-community') };
}

/** Desktop: /wifi-community and /wifi-suggestions open the drawer over the dashboard. */
export function OpenCommunity({ view = 'chat' }: { view?: 'chat' | 'suggestions' }) {
   useEffect(() => {
      useCommunityDrawer.getState().setOpen(true, view);
   }, [view]);
   return <Navigate to="/wifi" replace />;
}

/** /wifi-community: the full Community page on phones (a refresh stays on it); the drawer on bigger screens. */
export function CommunityRoute({ view = 'chat' }: { view?: 'chat' | 'suggestions' }) {
   const phone = useIsMobile(PHONE_MAX);
   // "Open in a new window" (Ray 2026-10-27): the whole chat in its own tab, any screen size.
   const [params] = useSearchParams();
   if (view === 'chat' && params.get('window') === '1')
      return (
         <Suspense fallback={null}>
            <CommunityPhonePage wide={!phone} />
         </Suspense>
      );
   if (!phone) return <OpenCommunity view={view} />;
   if (view === 'suggestions') return <PhoneSuggestions />;
   return (
      <Suspense fallback={null}>
         <CommunityPhonePage />
      </Suspense>
   );
}

// Unread while closed: the icon pulses so it is noticed (Ray 2026-10-27).
// Ray 2026-10-27: the border alone was not enough — the whole button flashes red, the icon white.
const pulse = keyframes`
   0%, 100% { background: #dc3545; border-color: #dc3545; color: #fff; box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.6); }
   50% { background: rgba(220, 53, 69, 0.15); border-color: #dc3545; color: #dc3545; box-shadow: 0 0 0 7px rgba(220, 53, 69, 0); transform: scale(1.08); }
`;
const ChatBtn = styled.button<{ $alert?: boolean }>`
   position: relative;
   width: 36px;
   height: 36px;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY13};
   background: ${colors.GRAY14};
   color: ${colors.GRAY1};
   cursor: pointer;
   display: grid;
   place-items: center;
   transition: background 0.15s, color 0.15s, border-color 0.15s;
   &:hover,
   &[aria-pressed='true'] {
      background: ${colors.BLUE_SUPER_LIGHT};
      color: ${colors.PRIMARY};
      border-color: ${colors.PRIMARY};
   }
   ${({ $alert }) =>
      $alert &&
      css`
         animation: ${pulse} 1.1s ease-in-out infinite;
         @media (prefers-reduced-motion: reduce) {
            animation: none;
            background: #dc3545;
            border-color: #dc3545;
            color: #fff;
         }
      `}
`;
// Same spot and size as the bell's badge beside it (Ray 2026-10-26: "not aligned").
const Badge = styled.span`
   position: absolute;
   top: 2px;
   right: 1px;
   min-width: 16px;
   height: 16px;
   padding: 0 4px;
   border-radius: 8px;
   display: grid;
   place-items: center;
   font-size: 10px;
   font-weight: 700;
   color: #fff;
   background: #dc3545;
   border: 2px solid var(--app-surface, #fff);
`;

/** Phones: /wifi-suggestions = the Community page showing Suggestions. */
function PhoneSuggestions() {
   useEffect(() => {
      useCommunityDrawer.getState().setView('suggestions');
   }, []);
   return <Navigate to="/wifi-community" replace />;
}

/** "Message" from any dashboard page (Ray 2026-10-27, Partners): opens the chat on a 1-on-1 with this person — the drawer on a computer, the Community page on a phone. */
export function useMessageMember() {
   const on = useCommunityFeatures().on('community');
   const phone = useIsMobile(PHONE_MAX);
   const navigate = useNavigate();
   return {
      on,
      message: (userId: string) => {
         useCommunityDrawer.getState().messageUser(userId);
         if (phone) navigate('/wifi-community');
      },
   };
}
